import React, { CSSProperties, PropsWithChildren } from "react";
import RefreshSpinner from "common/Lists/RefreshSpinner";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import styled, { useTheme } from "styled-components";
import useScrollView from "common/Scroll/useScrollView";

interface ScrollViewProps {
  onRefresh?: () => void;
  refreshing?: boolean;
  loading?: boolean;
  style?: CSSProperties;
  contentWrapperStyle?: CSSProperties;
  hideScrollBar?: boolean;
  ignoreExternalRefreshing?: boolean;
}

const ScrollView = ({
  onRefresh,
  refreshing,
  loading,
  children,
  style,
  contentWrapperStyle,
  hideScrollBar,
  ignoreExternalRefreshing,
}: PropsWithChildren<ScrollViewProps>) => {
  const theme = useTheme();
  const { scrollRef, scrollViewProps, spinnerProps, showSpinner } =
    useScrollView({
      refreshing,
      loading,
      onRefresh,
      ignoreExternalRefreshing,
    });

  return (
    <Wrapper
      ref={scrollRef}
      style={style}
      {...scrollViewProps}
      $hideScrollBar={hideScrollBar}
    >
      {showSpinner && <RefreshSpinner {...spinnerProps} />}
      <LoadingSpinnerOverlay
        isLoading={loading}
        spinnerSize="large"
        hideContentWhileLoading={loading}
        backgroundColor={theme.colors.layoutBgColor}
      >
        <ContentWrapper style={contentWrapperStyle}>{children}</ContentWrapper>
      </LoadingSpinnerOverlay>
    </Wrapper>
  );
};

export default ScrollView;

const Wrapper = styled.div<{ $hideScrollBar?: boolean }>`
  overflow-y: auto;
  position: relative;
  height: 100%;
  width: 100%;
  ${(p) =>
    p.$hideScrollBar &&
    `
    -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  `};
`;
const ContentWrapper = styled.div`
  padding: 24px 0;
`;
